import { ManualBackgroundCheckStatusChangeParams } from '@/backend/features/background-check';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus, BackgroundCheckType, backgroundCheckStatuses } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { AppContext } from '@/ui/Context';
import SingleBackgroundCheckBlock, {
    SingleBackgroundCheckBlockComponentRef,
} from '@/ui/features/background-check/components/background-check-user-account-section/components/BackgroundCheckBlock';
import { useRequestBackgroundCheckDetails } from '@/ui/features/background-check/hooks';
import { useSelectedPersonBackgroundCheckData } from '@/ui/hooks/useSelectedPersonBackgroundCheckData';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Row } from '@juvare/platform-shared/features/storage';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Alert, Box, Button, FormControl, Paper, SxProps, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface BackgroundCheckAccountComponentRef {
    getData: () => Partial<ExtendedAccountData>;
    submit: () => void;
}

const backgroundDataTransform = (nextStatus: BackgroundCheckStatus, handler: WidgetHandler) => {
    const updatedAt = handler?.formData?.updatedAt;

    return {
        updatedAt: updatedAt ? new Date(updatedAt).toLocaleDateString('en-US') : '',
        statusLabel: backgroundCheckStatuses[nextStatus] ?? 'Error',
        nextStatus: nextStatus,
    };
};

const headerSx: SxProps = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { sm: 'center' },
    gap: 2,
    p: { xs: 2, sm: 0 },
    pl: { sm: 2 },
    pr: { sm: 2 },
};

export interface BackgroundCheckAccountComponentProps {
    data?: ExtendedAccountData;
    accountId?: string;
    onSubmitSuccess?: (data: Partial<ExtendedAccountData>, response: unknown, submitParams?: unknown) => void;
}

const BackgroundCheckAccountComponent: React.ForwardRefRenderFunction<
    BackgroundCheckAccountComponentRef,
    BackgroundCheckAccountComponentProps
> = ({ accountId, data: accountData, onSubmitSuccess }, ref) => {
    const { t } = useTranslation();
    const functionClient = useFunctionClient();
    const appContext = React.useContext(AppContext);
    const { backgroundCheckConfig } = useSelector(getSettings);

    const backgroundCheckBlockRefs = useRef<(SingleBackgroundCheckBlockComponentRef | null)[]>([]);

    const { isAllowedToRequestBackgroundCheck, handleRequestBackgroundCheck } = useRequestBackgroundCheckDetails();
    const allowManageBackgroundCheckData = requireManageMembersAndAnySystemPermission(appContext.userState, 'manage-background-check-data');

    const { backgroundCheckData, reload } = useSelectedPersonBackgroundCheckData(accountId);

    const handleSubmitSuccess = async (actions?: unknown) => {
        await Promise.all(
            backgroundCheckBlockRefs.current.map(async (singleRef) => {
                if (singleRef) {
                    return singleRef?.reload();
                }

                return new Promise((resolve) => resolve(null));
            }),
        );

        await reload();

        onSubmitSuccess?.({}, {}, actions);
    };

    React.useImperativeHandle(ref, () => ({
        submit: handleSubmitSuccess,
        getData: () => reload,
    }));

    const handleSubmitBackgroundCheck = async (data: {
        nextStatus?: BackgroundCheckStatus;
        type?: BackgroundCheckType;
        updatedAt?: number;
    }) => {
        try {
            if (!accountId || !data.nextStatus || !data.type) {
                return;
            }

            const params: ManualBackgroundCheckStatusChangeParams = {
                accountId: accountId,
                nextStatus: data.nextStatus,
                type: data.type,
            };

            await functionClient.executeFunction({
                functionName: 'manualBackgroundCheckStatusChange',
                params: params,
            });

            await handleSubmitSuccess();
            showInfoSnackbar({
                message: t('backgroundCheck.messages.saved'),
                autoHideDuration: 2000,
            });
        } catch (e) {
            handleErrorDialog(e);
        }
    };

    const handleOpenEditStatusModal = (backgroundCheck: BackgroundCheckColumns) => {
        showFormDialog({
            title: t('backgroundCheck.labels.editBackgroundCheckStatus'),
            data: {
                type: backgroundCheck.type,
                nextStatus: backgroundCheck.status,
                updatedAt: backgroundCheck.updatedAt,
            },
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.advancedRadio',
                        key: 'type',
                        validate: {
                            required: true,
                        },
                        valueProvider: {
                            type: 'backgroundCheckEnabledTypes',
                        },
                    },
                    {
                        type: 'form.select',
                        key: 'nextStatus',
                        label: 'Background Check Status',
                        event: {
                            onChange: backgroundDataTransform,
                        },
                        validate: {
                            required: true,
                        },
                        valueProvider: {
                            type: 'backgroundCheckEnabledStatuses',
                            paramsFrom: '^type',
                        },
                        allowInvalidValues: true,
                    },
                ],
            },
            onSubmit: handleSubmitBackgroundCheck,
        });
    };

    const backgroundChecksByType = backgroundCheckData?.reduce(
        (checkByType, backgroundCheck) => {
            const configByType = backgroundCheckConfig.type?.[backgroundCheck.columns.type as BackgroundCheckType];

            if (configByType?.process === 'manual') {
                checkByType.manual.push(backgroundCheck);
            } else {
                checkByType.automatic.push(backgroundCheck);
            }

            return checkByType;
        },
        { manual: [] as Row<BackgroundCheckColumns>[], automatic: [] as Row<BackgroundCheckColumns>[] },
    );

    const hideBackgroundCheckEntries = !accountData?.consentBackgroundCheck && !backgroundChecksByType?.manual.length;

    return (
        <Box>
            {accountId && accountData && !hideBackgroundCheckEntries && (
                <>
                    {backgroundCheckData?.map((singleBackgroundCheck, i) => (
                        <FormControl
                            key={singleBackgroundCheck.id}
                            fullWidth={true}
                            variant='outlined'
                            data-test='background-check-panel-manual'
                        >
                            <Paper variant='outlined' sx={{ mb: 4 }}>
                                <Box sx={headerSx}>
                                    <Typography variant='subtitle1' sx={{ flexGrow: { sm: 1 }, pt: { sm: 2 }, pb: { sm: 2 } }}>
                                        {backgroundCheckConfig.type[singleBackgroundCheck.columns.type as BackgroundCheckType].label}
                                    </Typography>
                                    {isAllowedToRequestBackgroundCheck(accountData.systemStatus, true) && (
                                        <Button
                                            startIcon={<CampaignOutlinedIcon />}
                                            onClick={() =>
                                                handleRequestBackgroundCheck(
                                                    accountData,
                                                    singleBackgroundCheck.columns,
                                                    accountId,
                                                    handleSubmitSuccess,
                                                )
                                            }
                                            data-test='request-background-check-button'
                                            variant='outlined'
                                        >
                                            {t('backgroundCheck.labels.requestBackgroundCheck')}
                                        </Button>
                                    )}
                                </Box>
                                <SingleBackgroundCheckBlock
                                    ref={(ref) => {
                                        backgroundCheckBlockRefs.current[i] = ref;
                                    }}
                                    singleBackgroundCheckData={singleBackgroundCheck}
                                    accountId={accountId}
                                    accountData={accountData}
                                    allowManageBackgroundCheckData={allowManageBackgroundCheckData}
                                    backgroundCheckConfig={backgroundCheckConfig}
                                    handleOpenEditStatusModal={handleOpenEditStatusModal}
                                />
                            </Paper>
                        </FormControl>
                    ))}
                </>
            )}

            {hideBackgroundCheckEntries && <Alert severity='info'>{t('backgroundCheck.messages.userDidNotConsent')}</Alert>}
        </Box>
    );
};

export const BackgroundCheckAccountSection = React.forwardRef(BackgroundCheckAccountComponent);
